import React, { useEffect, useState, useRef } from 'react';
import { TreeGridComponent, ColumnsDirective, ColumnDirective, Inject, Edit } from '@syncfusion/ej2-react-treegrid';
import * as Inovx from '@shared';
import { RowDataBoundEventArgs, RowSelectingEventArgs } from '@syncfusion/ej2-react-grids';
import { DialogComponent, TooltipComponent } from '@syncfusion/ej2-react-popups';
import FacilityStore from '../../stores/FacilityStore';
import KnowledgeBookStore from '../../stores/KnowledgeBookStore';
import KnowledgeBookActions from '../../actions/KnowledgeBookActions';
import ClassificationTags from './ClassificationTags';
import ViewerActions from '../../actions/ViewerActions';
import viewerStore from '../../stores/ViewerStore';
import KnowledgeBookConstants from '@facility/constants/KnowledgeBookConstants';
import useAsyncActionLock from '@shared/hooks/useAsyncActionLock';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var InovxControls = Inovx.Shared.Controls;
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
interface TreeNode {
    ID: string;
    Name: string;
    ParentID?: string | null;
    ClassificationTags?: TreeNode[];
    isExpanded?: boolean;
    VersionToken?: string | null
}
/* ------------------ COMPONENT ------------------ */
interface KnowledgeBookFormProps {
    onCreated?: (knowledgeBook: IKnowledgeBook) => Promise<void>;
    onClosed?: () => void;
    selectedTags?: string | string[];
    onSelectedTagsChange?: (selectedIds: string[]) => void;
}
const KnowledgeBookForm: React.FC<KnowledgeBookFormProps> = ({
    onCreated,
    onClosed,
    selectedTags,
    onSelectedTagsChange,
}) => {
    const [kBForm, setKBForm] = useState<IKnowledgeBook | null>(KnowledgeBookStore.getKnowledgeBook());
    const [kBFormErrors, setKBFormErrors] = useState<{ Name: boolean; Description: boolean; ClassificationTags: boolean }>({ Name: false, Description: false, ClassificationTags: false });
    const [isAdding, setIsAdding] = useState<boolean>(KnowledgeBookStore.getIsAdding());
    const [isEditing, setIsEditing] = useState<boolean>(KnowledgeBookStore.getIsEditing());
    const [isModalOpen, setIsModalOpen] = useState<boolean>(KnowledgeBookStore.getKBModalVisible());
    const [selectedOntology, setSelectedOntology] = useState<IOntologyPart>(FacilityStore.getSelectedOntology());
    const [isActionInProgress, setActionInProgress] = useState<boolean>(false);
    const loadState = () => {
        setKBForm(KnowledgeBookStore.getKnowledgeBook())
        setIsAdding(KnowledgeBookStore.getIsAdding())
        setIsEditing(KnowledgeBookStore.getIsEditing())
        setIsModalOpen(KnowledgeBookStore.getKBModalVisible())
        setSelectedOntology(FacilityStore.getSelectedOntology())
    };
    const getIsActionInProgress = ()=> {
        return SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_EDIT) || SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_BOOK_ADD);
    }

    const systemChange = () => {
        setActionInProgress(getIsActionInProgress())
    }

    useEffect(() => {
        SystemStore.addChangeListener(systemChange);
        KnowledgeBookStore.addChangeListener(loadState);
        return () => {
            KnowledgeBookStore.removeChangeListener(loadState);
            SystemStore.removeChangeListener(systemChange);
        };
    }, []);

    const createKnowledgeBook = async () => {
        const res = await KnowledgeBookActions.createKnowledgeBook();
        if (!res) return;
        try {
            if (onCreated) {
                await onCreated(res);
                return;
            }
            await KnowledgeBookActions.openKnowledgeBook(res);
            const kv = await ViewerActions.createNewKnowledgeView(res);
            await ViewerActions.openKnowledgeView(kv.ID);
            ViewerActions.setKnowledgeViewDirty(true);
        } finally {
            onCreated && onClosed?.();
        }
    };

    const saveKnowledgeBook = async () => {
        if (isEditing && isKBFormValid()) {
            await KnowledgeBookActions.editKnowledgeBook();
        } else if (isAdding && isKBFormValid()) {
            await createKnowledgeBook();
        }
    }
    const saveKnowledgeBookAction = useAsyncActionLock(saveKnowledgeBook);
    const handleBeforeClose = (args: any) => {
        if (KnowledgeBookStore.getHasChanges()) {
            closeAddKB();
            args.cancel = true;
            return;
        } else {
            closeAddKB();
        }

    };

    const closeAddKB = () => {
        if (KnowledgeBookStore.getHasChanges()) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: async () => {
                    if (isEditing && isKBFormValid()) {
                        await KnowledgeBookActions.editKnowledgeBook();
                    } else if (isAdding && isKBFormValid()) {
                        await createKnowledgeBook();
                    }
                },
                cancelCallback: () => {
                    KnowledgeBookActions.beingFinish();
                    setKBFormErrors({ Name: false, Description: false, ClassificationTags: false });
                    onClosed?.();
                }
            });
        } else {
            KnowledgeBookActions.beingFinish();
            setKBFormErrors({ Name: false, Description: false, ClassificationTags: false });
            onClosed?.();
        }
    };

    const isKBFormValid = () => {
        let isValid = true;
        const errors = { Name: false, Description: false, ClassificationTags: false };

        if (!kBForm?.Name) {
            isValid = false;
            errors.Name = true;
        }

        if ((kBForm?.ClassificationTags ?? []).length === 0) {
            isValid = false;
            errors.ClassificationTags = true;
        }

        setKBFormErrors(errors);
        return isValid;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const currentForm = KnowledgeBookStore.getKnowledgeBook();
        if (currentForm) {
            const updatedForm = { ...currentForm, [name]: value };
            KnowledgeBookActions.updateKnowledgeBook(updatedForm);
        }
    };
    const setSelectedTags = (tags: string[]) => {
        const currentForm = KnowledgeBookStore.getKnowledgeBook();
        if (currentForm) {
            const updatedForm = { ...currentForm, ClassificationTags: tags };
            setKBForm(updatedForm);
            KnowledgeBookActions.updateKnowledgeBook(updatedForm);
        }
    }
    const renderHeader = () => (
        <div>
            <div className="e-icon-settings dlg-template">
                {InovxLoader.t("facility:knowledgeBook.knowledgeBook")}
            </div>
        </div>
    );
    const renderFooter = () => (
        <div>
            <div className="d-flex j-cc">
                <div>
                    <button
                        id="facilityApp-save-btn"
                        onClick={() => void saveKnowledgeBookAction.run()}
                        className="btn btn-primary"
                        disabled={isActionInProgress || saveKnowledgeBookAction.isInFlight}
                    >
                        {InovxLoader.t("facility:preferences.saveButton")}
                    </button>
                </div>
                <div className="ml-1">
                    <button
                        id="facilityApp-cancel-btn"
                        onClick={closeAddKB}
                        className="btn btn-default"
                        disabled={saveKnowledgeBookAction.isInFlight}
                    >
                        {InovxLoader.t("facility:preferences.cancelButton")}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <DialogComponent
            target={'#app'}
            width='550px'
            header={renderHeader}
            footerTemplate={renderFooter}
            showCloseIcon={true}
            visible={isModalOpen}
            beforeClose={handleBeforeClose}
            isModal={true}
            zIndex={110000}
        >
            <div className="dialogContent">
                {viewerStore.is3DViewerVisible() && <InovxControls.FormField title={`${InovxLoader.t("facility:knowledgeBook.ontology")}`} inputName='ontology'>
                    <input type="text" className="form-control" name='ontology' readOnly={true} value={selectedOntology?.Name} />

                    <div className="add-on-icon">
                        <TooltipComponent content={InovxLoader.t('facility:knowledgeBook.ontologyLimitKnowledgeViews').replace('__ontology__', selectedOntology?.Name || '')} opensOn='Hover' target='#hoverOntologyInfo' position="LeftBottom">
                            <i id='hoverOntologyInfo' className="info-icon"></i>
                        </TooltipComponent>
                    </div>
                </InovxControls.FormField>}

                <InovxControls.FormField
                    title={InovxLoader.t("facility:knowledgeBook.title")}
                    inputName='Name'
                    className={`${viewerStore.is3DViewerVisible() ? 'mt-1' : ''} ${kBFormErrors.Name ? 'has-error' : ''}`}
                >
                    <input
                        type="text"
                        onChange={handleInputChange}
                        value={kBForm.Name || ''}
                        className="form-control"
                        name="Name"
                        placeholder={InovxLoader.t("facility:form.knowledgeBookNamePlaceholder")}
                        autoComplete="off"
                    />
                </InovxControls.FormField>

                <InovxControls.FormField
                    title={InovxLoader.t("facility:knowledgeBook.knowledgeBookDescription")}
                    inputName='Description'
                    className={`mt-1 ${kBFormErrors.Description ? 'has-error' : ''}`}
                >
                    <textarea
                        onChange={handleInputChange}
                        value={kBForm.Description || ''}
                        className="form-control"
                        name="Description"
                        placeholder={InovxLoader.t("facility:form.knowledgeBookDescriptionPlaceholder")}
                    />
                </InovxControls.FormField>
                <div className={`kb-form-wrapper taxonomy-wrapper ${kBFormErrors.ClassificationTags ? 'has-error' : ''}`}>
                    <div className="form-field">
                        <label className="control-label mt-1">{InovxLoader.t("facility:knowledgeBook.classificationTags")}</label>
                    </div>
                    <ClassificationTags selectedTags={kBForm.ClassificationTags} onSelectedTagsChange={setSelectedTags} restrictToCurrentUserPermissions={true} defaultSelectEnabledTags={true} showSelectAll={true}
                    />
                    {kBFormErrors.ClassificationTags && <div className="form-error">{InovxLoader.t("facility:knowledgeBook.classificationTagsRequired")}</div>}
                </div>
            </div>
        </DialogComponent>
    );
};

export default KnowledgeBookForm;
